import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import CheckoutForm from './CheckoutForm';
import MockCheckoutForm from './MockCheckoutForm';
import { getData } from '../business_formating/LLC/LLC_Form/TotalPrice/calculateTotalPrice';
import { useConfig } from '../../../app/configcontext';
import { useSelector } from 'react-redux';
import { FaLock, FaInfoCircle } from 'react-icons/fa';
import { Tooltip } from 'antd';
import "./payment.scss";
import { useNavigate } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────────
// MOCK_PAYMENT_MODE = true  → Uses a fake card form, no real Stripe key needed.
//                             Full end-to-end flow works (form → backend → /confirm).
// MOCK_PAYMENT_MODE = false → Uses real Stripe. Set STRIPE_PK to your real key.
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_PAYMENT_MODE = false;
const STRIPE_PK = 'pk_live_51TgQEdKsHjyCqLUrUXKJqdC57veQ0P7lV70J6vJKTJQvSrDbw0C26gRhXpFgEnxH3Z2yEykLjbL1O7VcdB1P47Fy00QTgoGOXR';

const stripePromise = MOCK_PAYMENT_MODE ? null : loadStripe(STRIPE_PK);


export default function Payment() {
  const navigate = useNavigate();
  const config = useConfig();
  const business = useSelector((state) => state.business);

  const data = getData(business, config) || [];
  const prices = data.map((item) => item.price);
  const stateName = business?.businessLocation?.state?.toLowerCase() || '';

  let filingFees = 100; // Default
  let stateFilingFee = 100;
  let stateExpediteFee = 0;
  let priorityFee = 0;

  if (stateName === 'delaware') { stateFilingFee = 109; stateExpediteFee = 50; priorityFee = 30; filingFees = 189; }
  else if (stateName === 'maryland') { stateFilingFee = 100; stateExpediteFee = 50; priorityFee = 30; filingFees = 180; }
  else if (stateName === 'wyoming') { stateFilingFee = 100; stateExpediteFee = 0; priorityFee = 30; filingFees = 130; }
  else if (stateName === 'michigan') { stateFilingFee = 50; stateExpediteFee = 50; priorityFee = 30; filingFees = 130; }

  const subtotal = Object.values(prices).reduce((acc, price) => acc + price, 0);
  const total = subtotal + filingFees;

  const payment = "1";
  const userToken = localStorage.getItem("userToken");
  const email = business?.primaryContact?.email;

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to cancel this payment?';
      return e.returnValue;
    };

    const handleClick = (e) => {
      if (e.target.closest('.cancelModal')) return;

      const anchor = e.target.closest("a");
      if (anchor && anchor.href) {
        if (anchor.target === "_blank") return;

        const targetUrl = new URL(anchor.href, window.location.origin);

        if (
          targetUrl.origin === window.location.origin &&
          targetUrl.pathname !== window.location.pathname
        ) {
          e.preventDefault();
          e.stopPropagation();
          setPendingRoute(targetUrl.pathname + targetUrl.search + targetUrl.hash);
          setShowCancelModal(true);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  const confirmNavigation = () => {
    setShowCancelModal(false);
    localStorage.removeItem("stripeAccess");
    if (pendingRoute) {
      navigate(pendingRoute);
    }
  };

  const cancelNavigation = () => {
    setShowCancelModal(false);
    setPendingRoute(null);
  };

  useEffect(() => {
    if (!userToken) {
      navigate("/");
    } else if (payment) {
      // Renamed localStorage key to stripeAccess to reflect Stripe integration
      localStorage.setItem("stripeAccess", payment);
    }
  }, [payment, userToken, navigate]);

  const backBtn = () => {
    navigate(-1);
  };

  return (
    <div>
      <section className='payment'>
        <div className="payment-container">

          {/* Left Panel: Order Summary */}
          <div className="summary-panel">
            <button className="back-btn" onClick={backBtn}>
              <i className="fa fa-arrow-left"></i> Back
            </button>

            <div className="brand-logo">
              <img src="Assets/logo.png" alt="Logo" />
            </div>

            <div className="summary-header">
              <span className="summary-subtitle">PAYING TO</span>
              <h1 className="business-name">{business.businessDetails?.businessName || "Your Business"}</h1>
              {email && <span className="business-email">{email}</span>}
            </div>

            <div className="order-items">
              <h3>Order Summary</h3>
              <div className="items-list">
                {data.map((ele) => (
                  <div className="order-item-row" key={ele.name}>
                    <span className="item-name">{ele.name}</span>
                    <span className="item-price">${ele.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pricing-breakdown">
              <div className="breakdown-row">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="breakdown-row">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {business.businessLocation?.state || "State"} Filing Fees
                  <Tooltip
                    title={
                      <div style={{ padding: '6px 8px', minWidth: '220px', fontSize: '13px', lineHeight: '1.6' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#94a3b8' }}>State Filing Fee:</span>
                          <span style={{ fontWeight: 600, color: '#f8fafc' }}>${stateFilingFee.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#94a3b8' }}>State Expedite Fee:</span>
                          <span style={{ fontWeight: 600, color: '#f8fafc' }}>{stateExpediteFee ? `$${stateExpediteFee.toFixed(2)}` : 'N/A'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#94a3b8' }}>Oktobiz Priority Fee:</span>
                          <span style={{ fontWeight: 600, color: '#f8fafc' }}>${priorityFee.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #334155', paddingTop: '6px', marginTop: '6px', fontWeight: 700 }}>
                          <span style={{ color: '#f8fafc' }}>Total:</span>
                          <span style={{ color: '#38bdf8' }}>${filingFees.toFixed(2)}</span>
                        </div>
                      </div>
                    }
                    placement="topLeft"
                    trigger={['hover', 'click']}
                    color="#0f172a"
                    overlayInnerStyle={{ borderRadius: '8px', padding: '6px' }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', verticalAlign: 'middle', padding: '2px' }}>
                      <FaInfoCircle style={{ color: '#64748b', fontSize: '15px' }} />
                    </span>
                  </Tooltip>
                </span>
                <span>${filingFees.toFixed(2)}</span>
              </div>
              <div className="breakdown-row total-row">
                <span>Total Due</span>
                <span className="total-amount">${total.toFixed(2)}</span>
              </div>
            </div>

            <div className="trust-footer">
              <FaLock className="lock-icon" />
              <span>Secure SSL 256-Bit Encrypted Payment</span>
            </div>
          </div>

          {/* Right Panel: Payment Form */}
          <div className="payment-panel">
            <div className="payment-header">
              <h2>Card Details</h2>
              <p>Pay securely with your credit or debit card. No UPI or wallet methods are required.</p>
            </div>

            <div className="stripe-card-container">
              {MOCK_PAYMENT_MODE ? (
                <MockCheckoutForm total={total} />
              ) : (
                <Elements stripe={stripePromise}>
                  <CheckoutForm total={total} />
                </Elements>
              )}
            </div>
          </div>

        </div>
      </section>

      {showCancelModal && (
        <div className="cancelModalBackdrop">
          <div className="cancelModal">
            <h3>Cancel Payment?</h3>
            <p>Are you sure you want to leave this page? Your payment process will be canceled.</p>
            <div className="modalActions">
              <button className="btnCancel" onClick={cancelNavigation}>
                No, stay here
              </button>
              <button className="btnConfirm" onClick={confirmNavigation}>
                Yes, leave page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
